import { useInfiniteQuery } from "@tanstack/react-query";
import { useAuth } from "./use-auth";
import { apiClient } from "@/lib/api-client";
import type { ActivityFeedResponse } from "@/api/generated/client";

interface UseClaimActivityOptions {
  limit?: number;
  enabled?: boolean;
}

/**
 * Hook for fetching claim activity feed with cursor-based pagination
 */
export function useClaimActivity(claimId: number, options: UseClaimActivityOptions = {}) {
  const { firebaseUser } = useAuth();
  const { limit = 50, enabled = true } = options;

  const query = useInfiniteQuery<ActivityFeedResponse, Error>({
    queryKey: ["claim-activity", claimId, limit],
    queryFn: async ({ pageParam }) => {
      if (!claimId) {
        return { events: [], hasMore: false };
      }
      const response = await apiClient.activities.getClaimActivityFeed({
        claimId,
        limit,
        lastSeenEventId: pageParam as number | undefined,
      });
      return response;
    },
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) => {
      if (!lastPage.hasMore || !lastPage.nextCursor) {
        return undefined;
      }
      return lastPage.nextCursor;
    },
    enabled: !!claimId && !!firebaseUser && enabled,
  });

  // Flatten all pages into a single array
  const allEvents = query.data?.pages.flatMap(page => page.events) ?? [];
  const hasMore = query.data?.pages[query.data.pages.length - 1]?.hasMore ?? false;

  return {
    data: allEvents,
    isLoading: query.isLoading,
    error: query.error,
    hasMore,
    fetchNextPage: async () => {
      await query.fetchNextPage();
    },
    isFetchingNextPage: query.isFetchingNextPage,
  };
}
